export const CUSTOM_DOMAIN_CNAME_TARGET = 'domain.sortee.io'

/** Hosts do SPA padrão, mesmo se `VITE_PUBLIC_APP_URL` não estiver no bundle. */
const BUILTIN_MAIN_APP_HOSTS = new Set(['app.sortee.io'])

const HOSTNAME_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/

export function normalizeCustomDomainInput(value: string): string | null {
  const trimmed = value.trim().toLowerCase()
  if (trimmed === '') {
    return null
  }

  const withScheme = trimmed.includes('://') ? trimmed : `https://${trimmed}`
  let parsed: URL
  try {
    parsed = new URL(withScheme)
  } catch {
    return null
  }

  const host = parsed.hostname.replace(/\.$/, '')
  if (parsed.port !== '') {
    return null
  }
  if (!isValidCustomDomainHostname(host)) {
    return null
  }

  return host
}

export function isValidCustomDomainHostname(host: string): boolean {
  if (host.length === 0 || host.length > 253) {
    return false
  }
  if (host.includes(':')) {
    return false
  }
  if (!host.includes('.')) {
    return false
  }
  return HOSTNAME_PATTERN.test(host)
}

export function isMainAppHost(hostname: string, publicAppUrl: string): boolean {
  const host = hostname.trim().toLowerCase()
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return true
  }

  if (BUILTIN_MAIN_APP_HOSTS.has(host)) {
    return true
  }

  const configured = publicAppUrl.trim()
  if (configured === '') {
    return false
  }

  try {
    const configuredUrl = configured.includes('://') ? configured : `https://${configured}`
    return new URL(configuredUrl).hostname.replace(/\.$/, '').toLowerCase() === host
  } catch {
    return false
  }
}

/**
 * No domínio custom, as chamadas autenticadas usam a origem da página (Worker faz proxy de /api).
 * No app principal e em localhost, permanece a URL absoluta da API.
 */
export function resolveBrowserApiBaseUrl(
  hostname: string,
  configuredApiBaseUrl: string,
  publicAppUrl: string
): string {
  if (isMainAppHost(hostname, publicAppUrl)) {
    return configuredApiBaseUrl
  }

  if (hostname.trim().toLowerCase() === CUSTOM_DOMAIN_CNAME_TARGET) {
    return configuredApiBaseUrl
  }

  return ''
}

export function isReservedCustomDomainHost(host: string, publicAppUrl: string): boolean {
  const normalized = host.trim().toLowerCase()
  if (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '[::1]' ||
    normalized === CUSTOM_DOMAIN_CNAME_TARGET
  ) {
    return true
  }

  return isMainAppHost(normalized, publicAppUrl)
}

export function isCustomDomainCampaignPath(pathname: string, slug: string): boolean {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] !== 'campanha' || parts[1] !== slug) {
    return false
  }

  if (parts.length === 2) {
    return true
  }

  if (parts.length === 3) {
    return ['conta', 'checkout', 'perfil', 'ganhador'].includes(parts[2])
  }

  return false
}

export function shouldSkipPublicPageRedirectOnCustomDomain(
  customDomainSlug: string | null,
  campaignSlug: string | null | undefined
): boolean {
  if (!customDomainSlug || !campaignSlug) {
    return false
  }

  return customDomainSlug === campaignSlug
}

/** URL da landing neste host, ou `null` se o path já for do fluxo público da campanha. */
export function buildCustomDomainCampaignRedirectUrl(
  currentUrl: URL,
  boundSlug: string
): string | null {
  const slug = boundSlug.trim()
  if (slug === '' || isCustomDomainCampaignPath(currentUrl.pathname, slug)) {
    return null
  }

  const target = new URL(`/campanha/${encodeURIComponent(slug)}`, currentUrl.origin)
  currentUrl.searchParams.forEach((value, key) => {
    target.searchParams.set(key, value)
  })

  return target.toString()
}
